import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { QrcodeSharingScreen } from '@euler/app/flows/common/QrcodeSharing';
import { VehicleSubscriptionsScreen } from '@euler/app/flows/common/VehicleSubscriptionsScreen';
import { OrderDetailScreen } from '@euler/app/flows/order/pages/OrderDetailScreen';
import { ProductFlow } from '@euler/app/flows/products/ProductFlow';
import { ServiceRecordScreen } from '@euler/app/flows/record/pages/detail/ServiceRecordScreen';
import { ServiceRecordTimelinePage } from '@euler/app/flows/record/pages/events/ServiceRecordTimelinePage';
import { ServiceRecordMediaAlbumsPage } from '@euler/app/flows/record/pages/medias/ServiceRecordMediaAlbumsPage';
import { ServiceRecordMediasPage } from '@euler/app/flows/record/pages/medias/ServiceRecordMediasPage';
import { ServiceRecordPendingIssuesPage } from '@euler/app/flows/record/pages/pending-issues/ServiceRecordPendingIssuesPage';
import { ServiceRecordQuotationFormPage } from '@euler/app/flows/record/pages/quotation/ServiceRecordQuotationFormPage';
import { ServiceRecordsPage } from '@euler/app/flows/record/pages/records/ServiceRecordsPage';
import { ServiceRecordSettlementFormPage } from '@euler/app/flows/record/pages/settlement/ServiceRecordSettlementFormPage';
import {
  getServiceRecordResourceNamespace,
  ServiceRecordContextProvider,
} from '@euler/app/flows/record/ServiceRecordContext';
import { ErrorFallback } from '@euler/components/error';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { FontFamily } from '@euler/components/typography';
import { useRegisterActiveEntity } from '@euler/functions/ActiveEntityContext';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ScopedObservableSuspenseResourceContextProvider } from '@euler/utils/hooks';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import {
  createStackNavigator,
  TransitionPresets,
} from '@react-navigation/stack';
import * as Sentry from '@sentry/react';
import { StatusBar } from 'expo-status-bar';
import { memo } from 'react';
import { useTheme } from 'styled-components';

const Stack = createStackNavigator();

const Screens = {
  detail: ServiceRecordScreen,
  timeline: ServiceRecordTimelinePage,
  pendingIssues: ServiceRecordPendingIssuesPage,
  mediaAlbums: ServiceRecordMediaAlbumsPage,
  medias: ServiceRecordMediasPage,
  records: ServiceRecordsPage,
  _orderDetail: OrderDetailScreen,
  _qrcodeSharing: QrcodeSharingScreen,
  _vehicleSubscriptions: VehicleSubscriptionsScreen,
  _products: ProductFlow,
  _quotationForm: ServiceRecordQuotationFormPage,
  _settlementForm: ServiceRecordSettlementFormPage,
};

export type ServiceRecordNavParams = ScreenToParams<typeof Screens>;

type Props = {
  id: string;
};

export const ServiceRecordFlow = wrapNavigatorScreen(
  (props: Props) => {
    return <Content {...props} />;
  },
  {
    headerShown: false,
  },
);

const Content = memo((props: Props) => {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  // Publish the focused service record so out-of-flow surfaces (the floating
  // chat panel) can tell when their order is already on screen.
  useRegisterActiveEntity({ kind: 'serviceRecord', serviceRecordId: props.id });
  const resourceNamespace = getServiceRecordResourceNamespace(props.id);
  return (
    <Sentry.ErrorBoundary
      // @ts-ignore
      fallback={(params: any) => <ErrorFallback {...params} />}
    >
      <ScopedObservableSuspenseResourceContextProvider
        scope={resourceNamespace}
      >
        <ServiceRecordContextProvider serviceRecordId={props.id}>
          <BottomSheetModalProvider>
            <StatusBar style={'dark'} />
            <Stack.Navigator
              initialRouteName={'detail'}
              screenOptions={{
                ...TransitionPresets.SlideFromRightIOS,
                headerMode: 'float',
                headerTitleAlign: 'center',
                headerBackTitle: ' ',
                headerTitleStyle: {
                  fontSize: rpx(18),
                  fontFamily: FontFamily.NotoSans.Bold,
                },
                cardStyle: {
                  backgroundColor: theme.page.card.backgroundColor,
                },
                headerTitle: MultilineHeaderTitle,
                headerBackImage: () => <BackIcon />,
              }}
            >
              {spreadScreens(Stack.Screen, Screens, 'detail', {})}
            </Stack.Navigator>
          </BottomSheetModalProvider>
        </ServiceRecordContextProvider>
      </ScopedObservableSuspenseResourceContextProvider>
    </Sentry.ErrorBoundary>
  );
});
